import { sessionGeneration, endLocalSession, sessionCancelled } from "./sessionLifecycle.js";
import { getAppAttribution } from "./attributionController.js";
import { planningDestination, onboardingEntryRedirect } from "./onboardingNavigation.js";

export const normalizeAuthPath = (path) => path.replace(/\.html$/, "").replace(/\/$/, "");
export const isGuestAuthPage = (path) => ["/auth/log-in", "/auth/sign-up", "/auth/forgot-password"].includes(normalizeAuthPath(path));
// App pages a signed-in user may open before their first deposit is confirmed: after paying by bank
// transfer (or M-Pesa, before its callback lands) the invest flow sends them here to upload KYC.
export const isOpenBeforeFirstDeposit = (path) => ["/app/kyc-information"].includes(normalizeAuthPath(path));
export function routeDestination(response) {
  const login = response?.login;
  switch (login?.stage) {
    case "email_verification":
      if (typeof response.email !== "string" || !response.email) throw new Error("Missing verification email");
      return "/auth/verify-email?em=" + encodeURIComponent(response.email);
    case "onboarding": case "nonInvestor":
      if (login.preferred_experience === "wealth_tracker") return "/on-boarding/wealth-tracker";
      if (login.preferred_experience === "planning") {
        if (login.onboarding_profile_complete === true && typeof login.has_goal !== "boolean") return "/on-boarding/home";
        return planningDestination(login);
      }
      return "/on-boarding/home";
    case "investor": case "live": case "first": return "/app/dashboard";
    default: throw new Error("Unrecognized account stage");
  }
}
export const sessionRequestOptions = { skipAuthReauth: true, skipAuthWait: true, sessionManaged: true, cache: false, useCache: false, timeout: 10000 };
export function createSessionCoordinator({ store, window = globalThis.window }) {
  let pending;
  let logoutPending;
  let channel;
  const marker = "ap-session-ended";
  async function resolveRoute() {
    if (pending) return pending;
    const generation = sessionGeneration();
    const token = store.state.auth.token;
    const assertCurrent = () => { if (generation !== sessionGeneration() || token !== store.state.auth.token) throw sessionCancelled(); };
    pending = (async () => {
      if (!token) throw Object.assign(new Error("No session"), { response: { status: 401 } });
      const response = await store.dispatch("auth/loadRoute", sessionRequestOptions);
      assertCurrent();
      const status = await store.dispatch("auth/checkAccountStatus", sessionRequestOptions);
      assertCurrent();
      return status?.account_disabled ? "/auth/account-disabled" : routeDestination(response);
    })();
    try { return await pending; } finally { pending = null; }
  }
  function clearInvalid() {
    endLocalSession();
    store.commit("resetSession");
  }
  async function guard() {
    if (!store.state.auth.token) {
      if (store.state.auth.user) clearInvalid();
      return "anonymous";
    }
    try {
      const destination = await resolveRoute();
      window.location.replace(destination);
      return "authenticated";
    } catch (error) {
      if (error.response?.status !== 401) throw error;
      clearInvalid();
      getAppAttribution()?.capture();
      return "anonymous";
    }
  }
  function broadcast() {
    try { channel?.postMessage({ type: "logout" }); } catch { /* Optional. */ }
    try { window.localStorage.setItem(marker, JSON.stringify({ at: Date.now(), nonce: Math.random() })); } catch { /* Optional. */ }
  }
  function receive() {
    store.dispatch("auth/logout", { remote: true });
    if (normalizeAuthPath(window.location.pathname) !== "/auth/logout") window.location.replace("/auth/log-in");
  }
  function logout() {
    if (!logoutPending) {
      logoutPending = store.dispatch("auth/logout");
      broadcast();
    }
    return logoutPending;
  }
  async function logoutAndRedirect() {
    await logout();
    window.location.replace("/auth/logout");
  }
  try {
    channel = new window.BroadcastChannel("ap-session-v1");
    channel.onmessage = (event) => { if (event.data?.type === "logout") receive(); };
  } catch { /* Storage event is the fallback. */ }
  window.addEventListener("storage", (event) => { if (event.key === marker && event.newValue) receive(); });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) window.location.reload();
  });
  window.document.addEventListener("click", (event) => {
    const anchor = event.target?.closest?.("a[href]");
    if (!anchor) return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin || normalizeAuthPath(url.pathname) !== "/auth/logout") return;
    event.preventDefault();
    void logoutAndRedirect();
  }, true);
  return { resolveRoute, guard, logout, logoutAndRedirect };
}
let active;
export function initializeSessionCoordinator(options) { return active ||= createSessionCoordinator(options); }
export const getSessionCoordinator = () => active;

// Block exported Webflow forms before Vue or jQuery can wire their submission.
export async function guardAuthEntry(coordinator, window = globalThis.window) {
  const path = normalizeAuthPath(window.location.pathname);
  if (path === "/auth/logout") { await coordinator.logout(); return; }
  if (!isGuestAuthPage(path) && path !== "/auth/route") return;
  const document = window.document;
  const root = document.querySelector(".root") || document.querySelector("section.section-inner");
  if (root) { root.hidden = true; root.inert = true; }
  const panel = document.createElement("section");
  panel.setAttribute("role", "status");
  panel.style.cssText = "max-width:440px;margin:15vh auto;padding:24px;border-top:4px solid #fcc455;border-radius:12px;background:white;color:#083e5b;font-family:Montserrat,Arial,sans-serif";
  document.body.append(panel);
  await new Promise((resolve) => {
    async function attempt() {
      panel.textContent = "Checking your session...";
      try {
        const result = await coordinator.guard();
        if (result === "anonymous") {
          if (path === "/auth/route") { window.location.replace("/auth/log-in"); return; }
          panel.remove();
          if (root) { root.hidden = false; root.inert = false; }
          resolve();
        }
      } catch (error) {
        if (error.code === "ERR_CANCELED") return;
        panel.textContent = "We could not check your session. Please try again or sign out.";
        for (const [label, action] of [["Retry", attempt], ["Sign out", () => coordinator.logoutAndRedirect()]]) {
          const button = document.createElement("button");
          button.type = "button"; button.textContent = label;
          button.className = "button"; button.style.margin = "12px 12px 0 0";
          button.addEventListener("click", action); panel.append(button);
        }
      }
    }
    void attempt();
  });
}

export async function guardExperienceEntry(coordinator, window = globalThis.window) {
  const path = normalizeAuthPath(window.location.pathname);
  if (!path.startsWith("/app/") && !path.startsWith("/on-boarding/")) return;
  if (!coordinator || !window.document) return;
  if (!window.$store?.state?.auth?.token) { window.location.replace("/auth/log-in"); return; }
  const root = window.document.querySelector(".root") || window.document.querySelector("section.section-inner");
  if (root) { root.hidden = true; root.inert = true; }
  const panel = window.document.createElement("section");
  panel.setAttribute("role", "status");
  panel.style.cssText = "max-width:440px;margin:15vh auto;padding:24px;background:white;color:#083e5b;font-family:Montserrat,Arial,sans-serif";
  window.document.body.append(panel);
  async function attempt() {
    panel.textContent = "Checking your account...";
    try {
      const destination = await coordinator.resolveRoute();
      const isInvestor = destination.startsWith("/app/");
      const redirect = destination.startsWith("/auth/")
        ? destination
        : path.startsWith("/app/") && !isInvestor
          ? (isOpenBeforeFirstDeposit(path) ? null : destination)
          : path.startsWith("/on-boarding/") && isInvestor
            ? (path.endsWith("/wealth-tracker") || path.endsWith("/add-an-asset") ? "/app/wealth-tracker" : path.endsWith("/kyc-information") ? "/app/kyc-information" : "/app/dashboard")
            : onboardingEntryRedirect(path + (window.location.search || ""), window.$store.state.auth.user?.login);
      if (redirect) { window.location.replace(redirect); return; }
      panel.remove();
      if (root) { root.hidden = false; root.inert = false; }
    } catch (error) {
      if (error.response?.status === 401) { window.location.replace("/auth/log-in"); return; }
      panel.textContent = "We could not check your account. Please try again.";
      const retry = window.document.createElement("button");
      retry.type = "button"; retry.textContent = "Retry"; retry.className = "button";
      retry.addEventListener("click", attempt); panel.append(retry);
    }
  }
  await attempt();
}
